import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'wouter';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from '@/hooks/use-toast';
import { apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Edit, Mail, Plus, Trash2, AlertTriangle, Calendar, RefreshCw, GitBranch, Zap, Check, Settings, Bot, Code, ArrowRight, Play, Pause, Eye, FileText, CheckCircle2, Users, Tag as TagIcon, X, LogOut } from 'lucide-react';
import SuperAdminLayout from '@/components/admin/super-admin-layout';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';

// Create a schema for template creation/update
const templateSchema = z.object({
  name: z.string().min(3, "Template name must be at least 3 characters"),
  type: z.string(),
  subject: z.string().min(3, "Subject must be at least 3 characters"),
  body: z.string().min(10, "Template body must be at least 10 characters"),
  description: z.string().optional(),
  targetCategory: z.string().optional(),
  startDate: z.string().optional().nullable(),
  endDate: z.string().optional().nullable(),
});

type TemplateFormValues = z.infer<typeof templateSchema>;

// Template Type Options
const templateTypes = [
  { value: "notification", label: "Notification" },
  { value: "marketing", label: "Marketing" },
  { value: "transactional", label: "Transactional" },
  { value: "welcome", label: "Welcome" },
  { value: "reminder", label: "Reminder" }
];

// Target Category helper
const getTargetCategoryLabel = (category: string) => {
  switch (category) {
    case 'trial': return 'Trial Period';
    case 'expired': return 'Expired License';
    case 'expiring': return 'License Expiring Soon';
    case 'operational': return 'Operational (Active Customers)';
    case 'all': return 'All Users';
    default: return 'Not Configured';
  }
};

const getTargetCategoryVariant = (category: string) => {
  switch (category) {
    case 'trial': return 'secondary';
    case 'expired': return 'destructive';
    case 'expiring': return 'outline';
    case 'operational': return 'default';
    default: return 'default';
  }
};

// Trigger Action helper
export const getTriggerActionLabel = (action: string) => {
  switch (action) {
    case 'still_not_opened': return 'Email Still Not Opened';
    case 'link_not_clicked': return 'Link Not Clicked';
    case 'email_in_spam': return 'Email Marked as Spam';
    case 'email_delivered': return 'Email Delivered Successfully';
    case 'link_clicked': return 'Link Clicked in Email';
    case 'subscription_expired': return 'User Subscription Expired';
    default: return action.replace(/_/g, ' ');
  }
};

// Create / Edit Template Dialog Component
const TemplateDialog = ({ template, trigger }: { template?: any; trigger: React.ReactNode }) => {
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();
  const isEditing = !!template;
  
  const form = useForm<TemplateFormValues>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      name: template?.name || "",
      type: template?.type || "notification",
      subject: template?.subject || "",
      body: template?.body || "",
      description: template?.description || "",
      targetCategory: template?.targetCategory || "trial",
      startDate: template?.startDate ? new Date(template.startDate).toISOString().split('T')[0] : "",
      endDate: template?.endDate ? new Date(template.endDate).toISOString().split('T')[0] : "",
    }
  });

  const saveMutation = useMutation({
    mutationFn: (data: TemplateFormValues) => {
      const payload = {
        ...data,
        startDate: data.startDate || null,
        endDate: data.endDate || null,
      };
      
      return apiRequest(
        isEditing ? `/api/admin/email/templates/${template.id}` : '/api/admin/email/templates',
        {
          method: isEditing ? 'PUT' : 'POST',
          data: payload
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/email/templates'] });
      toast({
        title: isEditing ? "Template updated" : "Template created",
        description: `Your email template has been ${isEditing ? 'updated' : 'created'} successfully.`,
      });
      if (!isEditing) form.reset();
      setOpen(false);
    },
    onError: (error) => {
      console.error("Error saving template:", error);
      toast({
        title: "Failed to save template",
        description: "There was an error saving your template. Please try again.",
        variant: "destructive"
      });
    }
  });
  
  const onSubmit = (data: TemplateFormValues) => {
    saveMutation.mutate(data);
  };
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="w-[95vw] sm:w-[90vw] md:w-[650px] max-w-[95vw] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Edit Email Template" : "Create New Email Template"}</DialogTitle>
          <DialogDescription>
            {isEditing ? "Modify your email template details and scheduling settings." : "Create a reusable email template that can be triggered manually or automatically."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="name">Template Name</Label>
            <Input
              id="name"
              placeholder="Welcome Trial Email"
              {...form.register("name")}
            />
            {form.formState.errors.name && (
              <p className="text-red-500 text-xs">{form.formState.errors.name.message}</p>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="type">Template Type</Label>
              <Select 
                defaultValue={form.getValues("type")} 
                onValueChange={(value) => form.setValue("type", value)}
              >
                <SelectTrigger id="type">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {templateTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="targetCategory">Target Category of Users</Label>
              <Select 
                defaultValue={form.getValues("targetCategory")} 
                onValueChange={(value) => form.setValue("targetCategory", value)}
              >
                <SelectTrigger id="targetCategory">
                  <SelectValue placeholder="Select target category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="trial">Trial Period</SelectItem>
                  <SelectItem value="expired">Expired License</SelectItem>
                  <SelectItem value="expiring">License Expiring Soon</SelectItem>
                  <SelectItem value="operational">Operational (Active Customers)</SelectItem>
                  <SelectItem value="all">All Users</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 border-t pt-3">
            <div className="space-y-2">
              <Label htmlFor="startDate">Start Date (Optional)</Label>
              <Input
                id="startDate"
                type="date"
                {...form.register("startDate")}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="endDate">End Date (Optional)</Label>
              <Input
                id="endDate"
                type="date"
                {...form.register("endDate")}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">Subject Line</Label>
            <Input
              id="subject"
              placeholder="Welcome to SAASGAMA!"
              {...form.register("subject")}
            />
            {form.formState.errors.subject && (
              <p className="text-red-500 text-xs">{form.formState.errors.subject.message}</p>
            )}
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="body">Email Body (HTML/Markdown)</Label>
            <Textarea
              id="body"
              placeholder="Write your email contents here..."
              className="min-h-[160px]"
              {...form.register("body")}
            />
            {form.formState.errors.body && (
              <p className="text-red-500 text-xs">{form.formState.errors.body.message}</p>
            )}
            <div className="text-[11px] text-gray-500 bg-gray-50 p-2 rounded border">
              <span className="font-semibold">Available placeholders:</span>
              <code className="mx-1 px-1 bg-gray-200 rounded">{"{{name}}"}</code> (User Full Name),
              <code className="mx-1 px-1 bg-gray-200 rounded">{"{{organization}}"}</code> (Tenant Name),
              <code className="mx-1 px-1 bg-gray-200 rounded">{"{{date}}"}</code> (Current Date)
            </div>
          </div>

          <div className="flex items-center justify-end gap-2 border-t pt-3 mt-4">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => setOpen(false)}
              disabled={saveMutation.isPending}
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={saveMutation.isPending}
            >
              {saveMutation.isPending ? "Saving..." : isEditing ? "Save Changes" : "Create Template"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Test Trigger Button Component
const TestTriggerButton = ({ templateId }: { templateId: number }) => {
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  
  const handleTrigger = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/admin/email/templates/${templateId}/trigger`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      
      const result = await response.json();
      
      if (result.success) {
        toast({
          title: "Trigger Executed Successfully",
          description: result.message || `Test emails triggered. Sent: ${result.sentCount}, Errors: ${result.errorCount}`,
        });
        queryClient.invalidateQueries({ queryKey: ['/api/admin/email/templates'] });
      } else {
        toast({
          title: "Failed to run trigger",
          description: result.message || "An error occurred.",
          variant: "destructive"
        });
      }
    } catch (error) {
      console.error("Error executing trigger:", error);
      toast({
        title: "Trigger execution failed",
        description: "Could not establish connection to template trigger service.",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button 
      variant="outline" 
      size="sm" 
      onClick={handleTrigger}
      disabled={loading}
      className="h-8 text-xs font-semibold text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 border-indigo-200"
    >
      <RefreshCw className={`mr-1 h-3 w-3 ${loading ? 'animate-spin' : ''}`} />
      {loading ? "Triggering..." : "Test Trigger"}
    </Button>
  );
};

// Template List Component — receives data from parent so the query stays alive across all tabs
const TemplateList = ({ data, isLoading, isError }: { data: any; isLoading: boolean; isError: boolean }) => {
  const queryClient = useQueryClient();
  
  // Delete template mutation
  const deleteMutation = useMutation({
    mutationFn: (id: number) => {
      return apiRequest(`/api/admin/email/templates/${id}`, {
        method: 'DELETE'
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/email/templates'] });
      toast({
        title: "Template deleted",
        description: "The email template has been deleted successfully."
      });
    },
    onError: (error) => {
      console.error("Error deleting template:", error);
      toast({
        title: "Failed to delete template",
        description: "There was an error deleting the template. Please try again.",
        variant: "destructive"
      });
    }
  });

  // Simulate opens mutation (local dev: marks all unopened records as opened)
  const simulateOpensMutation = useMutation({
    mutationFn: (templateId: number) =>
      apiRequest(`/api/admin/email/templates/${templateId}/simulate-opens`, { method: 'POST' }),
    onSuccess: (data: any) => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/email/templates'] });
      toast({
        title: "Opens Simulated ✅",
        description: data?.message || 'Open records updated. Metrics refreshed.',
      });
    },
    onError: () => {
      toast({ title: 'Failed to simulate opens', variant: 'destructive' });
    },
  });
  
  const handleDelete = (id: number) => {
    if (confirm("Are you sure you want to delete this template?")) {
      deleteMutation.mutate(id);
    }
  };
  
  if (isLoading) {
    return <div className="flex justify-center py-8">Loading templates...</div>;
  }
  
  if (isError) {
    return (
      <Card className="mt-4">
        <CardContent className="pt-6">
          <div className="flex items-center justify-center p-4">
            <AlertTriangle className="mr-2 h-5 w-5 text-amber-500" />
            <p>Failed to load email templates. Please try again later.</p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  if (!data?.templates || data.templates.length === 0) {
    return (
      <Card className="mt-4">
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center p-8 text-center">
            <Mail className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium">No templates found</h3>
            <p className="text-sm text-gray-500 mt-2">
              Create your first email template to get started with automated, targeted campaign communications.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="w-full">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50/50">
            <TableHead className="pl-4">Template Details</TableHead>
            <TableHead>Target Category</TableHead>
            <TableHead>Trigger Schedule</TableHead>
            <TableHead>Delivery Metrics</TableHead>
            <TableHead className="text-right pr-4">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.templates.map((template: any) => {
            const sent = template.sentCount || 0;
            const opened = template.openCount || 0;
            const openRate = sent > 0 ? Math.round((opened / sent) * 100) : 0;
            
            return (
              <TableRow key={template.id} className="hover:bg-slate-50/30">
                <TableCell className="pl-4 py-3">
                  <div className="font-semibold text-slate-800 text-sm">{template.name}</div>
                  <div className="text-[10px] text-slate-400 mt-0.5 font-medium">
                    Updated: {new Date(template.updatedAt).toLocaleDateString()}
                  </div>
                </TableCell>
                <TableCell className="py-3">
                  <Badge variant={getTargetCategoryVariant(template.targetCategory)} className="text-xs px-2 py-0.5">
                    {getTargetCategoryLabel(template.targetCategory)}
                  </Badge>
                </TableCell>
                <TableCell className="py-3">
                  <div className="space-y-0.5">
                    <div className="capitalize text-xs font-semibold text-slate-700">
                      {template.triggerFrequency === 'none' || !template.triggerFrequency
                        ? 'Manual' 
                        : `${template.triggerFrequency}${template.triggerLimitCount ? ` (${template.triggerLimitCount}x)` : ''}`}
                    </div>
                    {(template.startDate || template.endDate) && (
                      <div className="text-[10px] text-slate-500 flex items-center gap-1 font-medium">
                        <Calendar className="h-3 w-3 inline" />
                        {template.startDate ? new Date(template.startDate).toLocaleDateString() : 'Start'} 
                        {'-'} 
                        {template.endDate ? new Date(template.endDate).toLocaleDateString() : 'End'}
                      </div>
                    )}
                  </div>
                </TableCell>
                <TableCell className="py-3">
                  <div className="flex items-center gap-3">
                    <div className="min-w-[60px]">
                      <div className="text-xs font-semibold text-slate-700">{sent} sent</div>
                      <div className="text-[10px] text-slate-500 mt-0.5 font-medium">
                        {opened} opened
                      </div>
                    </div>
                    <div className="flex flex-col items-start gap-1">
                      <Badge variant={openRate > 25 ? "default" : "outline"} className={`font-mono text-[10px] h-5 px-1.5 shrink-0 ${openRate > 25 ? 'bg-emerald-500 hover:bg-emerald-600 text-white border-0' : openRate > 0 ? 'text-amber-600 border-amber-200 bg-amber-50' : 'text-slate-600'}`}>
                        {openRate}% Rate
                      </Badge>
                      {sent > opened && (
                        <button
                          onClick={() => simulateOpensMutation.mutate(template.id)}
                          disabled={simulateOpensMutation.isPending}
                          className="text-[9px] text-indigo-500 hover:text-indigo-700 underline underline-offset-2 font-medium leading-none disabled:opacity-50"
                          title={`${sent - opened} email(s) not yet tracked as opened. Click to simulate for local dev testing.`}
                        >
                          {simulateOpensMutation.isPending ? 'Simulating...' : `Simulate ${sent - opened} Open${sent - opened > 1 ? 's' : ''}`}
                        </button>
                      )}
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-right pr-4 py-3">
                  <div className="inline-flex items-center gap-1.5 justify-end">
                    <TestTriggerButton templateId={template.id} />
                    
                    <TemplateDialog 
                      template={template} 
                      trigger={
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="h-8 w-8 p-0 text-slate-500 hover:text-slate-900 hover:bg-slate-100"
                        >
                          <Edit className="h-4 w-4" />
                          <span className="sr-only">Edit</span>
                        </Button>
                      }
                    />
                    
                    <Button 
                      variant="ghost" 
                      size="sm" 
                      className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50" 
                      onClick={() => handleDelete(template.id)}
                      disabled={deleteMutation.isPending}
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="sr-only">Delete</span>
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

// ─── Vertical Automation Flow (Trigger → Condition → Yes/No → Action/Exit) ──
// Replaces the old free-drag node canvas. That one let you add Delay/
// Webhook nodes and type arbitrary "condition expressions," but none of it
// was ever persisted or read by the backend — only the trigger dropdown and
// the email/tag pickers were real. This version shows exactly what the
// workflow actually does: one trigger, one tag-based condition, and up to
// two branches (Yes/No), each ending in a real send or a real exit.

const FlowStepCard = ({
  icon, iconBg, stepLabel, title, completed, muted, children,
}: {
  icon: React.ReactNode; iconBg: string; stepLabel: string; title: string;
  completed?: React.ReactNode; muted?: boolean; children?: React.ReactNode;
}) => (
  <div className={`w-64 rounded-xl border shadow-sm bg-white ${muted ? 'border-slate-200 opacity-80' : 'border-slate-200'}`}>
    <div className="flex items-center gap-2 px-3 pt-3 pb-2">
      <span className={`p-1.5 rounded-lg text-white shrink-0 ${iconBg}`}>{icon}</span>
      <div className="min-w-0">
        <div className="text-[10px] font-semibold text-slate-400 uppercase tracking-wide">{stepLabel}</div>
        <div className="text-sm font-bold text-slate-800 truncate">{title}</div>
      </div>
    </div>
    {children && <div className="px-3 pb-3 space-y-1.5">{children}</div>}
    {completed !== undefined && (
      <div className="px-3 py-1.5 border-t bg-slate-50/60 rounded-b-xl text-[10px] font-semibold text-slate-500 flex items-center justify-between">
        <span>Completed</span>
        <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 min-w-[1.25rem] text-center">{completed}</span>
      </div>
    )}
  </div>
);

// A connector line with a disabled "+" — visually matches a real automation
// builder's "insert a step here," but clicking it explains why it's inert:
// this engine supports one trigger + one condition per workflow, not
// arbitrary chains. Chain multiple workflows via Apply Tag / Target Tag
// instead (covered elsewhere in this dialog).
const FlowConnector = ({ withAddButton = true }: { withAddButton?: boolean }) => (
  <div className="flex flex-col items-center">
    <div className="w-px h-5 bg-slate-300" />
    {withAddButton && (
      <button
        type="button"
        title="This engine runs one trigger + one condition per workflow, not an arbitrary chain. To add another step, create a second workflow and set its Target Tag to whatever this one applies on send."
        className="w-6 h-6 rounded-full bg-slate-300 text-white flex items-center justify-center cursor-help hover:bg-slate-400 transition-colors"
      >
        <Plus className="h-3 w-3" />
      </button>
    )}
    <div className="w-px h-5 bg-slate-300" />
  </div>
);

const WorkflowVisualCanvas = ({
  workflowName, setWorkflowName,
  templateId, setTemplateId,
  triggerAction, setTriggerAction,
  templates,
  allTags,
  targetTagId, setTargetTagId,
  applyTagId, setApplyTagId,
  onCreateTag,
  elseTemplateId, setElseTemplateId,
  elseApplyTagId, setElseApplyTagId,
  workflowId,
}: {
  workflowName: string; setWorkflowName: (v: string) => void;
  templateId: string;   setTemplateId: (v: string) => void;
  triggerAction: string; setTriggerAction: (v: string) => void;
  templates: any[];
  allTags: { id: number; name: string; userCount: number }[];
  targetTagId: number | null; setTargetTagId: (v: number | null) => void;
  applyTagId: number | null; setApplyTagId: (v: number | null) => void;
  onCreateTag: (name: string, setter: (id: number) => void) => void;
  elseTemplateId: string; setElseTemplateId: (v: string) => void;
  elseApplyTagId: number | null; setElseApplyTagId: (v: number | null) => void;
  workflowId?: number;
}) => {
  const [showElseAction, setShowElseAction] = useState(!!elseTemplateId);

  // Real per-branch send counts for an existing workflow (a brand-new,
  // unsaved one has nothing to fetch yet — cards just show "New").
  const recipientsQuery = useQuery({
    queryKey: [`/api/admin/email/workflows/${workflowId}/recipients`],
    queryFn: async () => {
      const res = await fetch(`/api/admin/email/workflows/${workflowId}/recipients`);
      if (!res.ok) throw new Error('Failed to fetch recipients');
      return res.json();
    },
    enabled: !!workflowId,
  });
  const sentCountsByTemplate: Record<number, number> = recipientsQuery.data?.sentCountsByTemplate || {};
  const totalSent = Object.values(sentCountsByTemplate).reduce((a: number, b: number) => a + b, 0);
  const ifSentCount = templateId ? (sentCountsByTemplate[parseInt(templateId)] || 0) : 0;
  const elseSentCount = elseTemplateId ? (sentCountsByTemplate[parseInt(elseTemplateId)] || 0) : 0;

  const templateName = (id: string) => templates?.find((t: any) => t.id.toString() === id)?.name || 'Select a template';

  const emailActionCard = (
    label: string,
    tplId: string, setTplId: (v: string) => void,
    tagId: number | null, setTagId: (v: number | null) => void,
    sentCount: number,
  ) => (
    <FlowStepCard icon={<Mail className="h-4 w-4" />} iconBg="bg-emerald-500" stepLabel={label} title={templateName(tplId)} completed={workflowId ? sentCount : 'New'}>
      <select className="w-full text-xs rounded border border-slate-200 bg-white p-1.5" value={tplId} onChange={e => setTplId(e.target.value)}>
        <option value="">Select a template…</option>
        {templates?.map((t: any) => <option key={t.id} value={t.id.toString()}>{t.name}</option>)}
      </select>
      <TagPicker
        allTags={allTags}
        appliedTagIds={tagId ? [tagId] : []}
        onToggleTag={(tid, isApplied) => setTagId(isApplied ? null : tid)}
        onCreateTag={(name) => onCreateTag(name, setTagId)}
        trigger={
          <button className="w-full text-xs rounded border border-emerald-200 bg-emerald-50 text-emerald-900 p-1.5 text-left truncate flex items-center gap-1.5">
            <TagIcon className="h-3 w-3 shrink-0" />
            {tagId ? `+${allTags.find(t => t.id === tagId)?.name || '…'} on send` : 'No tag applied'}
          </button>
        }
      />
    </FlowStepCard>
  );

  return (
    <div className="flex flex-col items-center py-6 px-4 bg-slate-50/60 rounded-xl border border-slate-200">
      <div className="w-full max-w-sm mb-5">
        <Input
          value={workflowName}
          onChange={e => setWorkflowName(e.target.value)}
          placeholder="Workflow name (e.g. VIP Follow-up)"
          className="text-center font-semibold bg-white"
        />
      </div>

      <FlowStepCard icon={<Zap className="h-4 w-4" />} iconBg="bg-indigo-500" stepLabel="Trigger" title={getTriggerActionLabel(triggerAction)} completed={workflowId ? totalSent : 'New'}>
        <select className="w-full text-xs rounded border border-slate-200 bg-white p-1.5 font-medium text-slate-700" value={triggerAction} onChange={e => setTriggerAction(e.target.value)}>
          <option value="still_not_opened">Still Not Opened</option>
          <option value="link_not_clicked">Link Not Clicked</option>
          <option value="email_delivered">Delivered Successfully</option>
          <option value="email_opened">Email Opened</option>
          <option value="email_in_spam">In Spam Folder</option>
          <option value="subscription_expired">Subscription Expired</option>
        </select>
      </FlowStepCard>

      <FlowConnector />

      <FlowStepCard icon={<GitBranch className="h-4 w-4" />} iconBg="bg-amber-500" stepLabel="Condition" title="User is tagged">
        <TagPicker
          allTags={allTags}
          appliedTagIds={targetTagId ? [targetTagId] : []}
          onToggleTag={(tagId, isApplied) => setTargetTagId(isApplied ? null : tagId)}
          onCreateTag={(name) => onCreateTag(name, setTargetTagId)}
          trigger={
            <button className="w-full text-xs rounded border border-amber-200 bg-amber-50 text-amber-900 p-1.5 text-left truncate flex items-center gap-1.5 font-medium">
              <TagIcon className="h-3 w-3 shrink-0" />
              {targetTagId ? allTags.find(t => t.id === targetTagId)?.name || '…' : 'Any user (no condition set)'}
            </button>
          }
        />
      </FlowStepCard>

      {targetTagId ? (
        <>
          <div className="w-px h-5 bg-slate-300" />
          <div className="flex gap-10 md:gap-16 w-full justify-center items-start">
            <div className="flex flex-col items-center">
              <span className="px-3 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-xs font-bold border border-emerald-200">Yes</span>
              <div className="w-px h-5 bg-slate-300" />
              {emailActionCard('Action', templateId, setTemplateId, applyTagId, setApplyTagId, ifSentCount)}
            </div>

            <div className="flex flex-col items-center">
              <span className="px-3 py-0.5 rounded-full bg-red-100 text-red-700 text-xs font-bold border border-red-200">No</span>
              <div className="w-px h-5 bg-slate-300" />
              {showElseAction ? (
                <div className="flex flex-col items-center gap-1.5">
                  {emailActionCard('Action', elseTemplateId, setElseTemplateId, elseApplyTagId, setElseApplyTagId, elseSentCount)}
                  <button
                    type="button"
                    onClick={() => { setShowElseAction(false); setElseTemplateId(''); setElseApplyTagId(null); }}
                    className="text-[10px] text-slate-400 hover:text-red-600 underline"
                  >
                    Remove — exit instead
                  </button>
                </div>
              ) : (
                <FlowStepCard icon={<LogOut className="h-4 w-4" />} iconBg="bg-slate-400" stepLabel="Exit Automation" title="No further action" muted>
                  <button
                    type="button"
                    onClick={() => setShowElseAction(true)}
                    className="text-xs text-indigo-600 hover:text-indigo-700 font-semibold flex items-center gap-1"
                  >
                    <Plus className="h-3 w-3" /> Send a different email instead
                  </button>
                </FlowStepCard>
              )}
            </div>
          </div>
        </>
      ) : (
        <>
          <FlowConnector withAddButton={false} />
          {emailActionCard('Action', templateId, setTemplateId, applyTagId, setApplyTagId, ifSentCount)}
        </>
      )}
    </div>
  );
};

// Workflow Dialog (Add / Edit Workflow)
const WorkflowDialog = ({ workflow, templates, trigger }: { workflow?: any; templates: any[]; trigger: React.ReactNode }) => {
  const [open, setOpen] = useState(false);
  const isEditing = !!workflow;
  const queryClient = useQueryClient();

  const [workflowName, setWorkflowName] = useState(workflow?.name || "");
  const [templateId, setTemplateId] = useState(workflow?.templateId?.toString() || (templates?.[0]?.id?.toString() || ""));
  const [triggerAction, setTriggerAction] = useState(workflow?.triggerAction || "still_not_opened");
  const [status, setStatus] = useState(workflow?.status || "draft");
  const [frequency, setFrequency] = useState(workflow?.frequency || "once");
  const [notOpenedDays, setNotOpenedDays] = useState<number | ''>(workflow?.notOpenedDays ?? 2);
  const [notClickedDays, setNotClickedDays] = useState<number | ''>(workflow?.notClickedDays ?? 2);
  const [frequencyCount, setFrequencyCount] = useState<number | ''>(workflow?.frequencyCount ?? 1);
  const [targetTagId, setTargetTagId] = useState<number | null>(workflow?.targetTagId ?? null);
  const [applyTagId, setApplyTagId] = useState<number | null>(workflow?.applyTagId ?? null);
  const [elseTemplateId, setElseTemplateId] = useState<string>(workflow?.elseTemplateId?.toString() || "");
  const [elseApplyTagId, setElseApplyTagId] = useState<number | null>(workflow?.elseApplyTagId ?? null);

  const tagsQuery = useQuery({
    queryKey: ['/api/admin/tags'],
    queryFn: async () => {
      const response = await fetch('/api/admin/tags');
      if (!response.ok) throw new Error('Failed to fetch tags');
      return response.json();
    }
  });
  const allTags: { id: number; name: string; userCount: number }[] = tagsQuery.data?.tags || [];

  const createTagMutation = useMutation({
    mutationFn: (name: string) => apiRequest('/api/admin/tags', { method: 'POST', data: { name } }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['/api/admin/tags'] }),
    onError: () => toast({ title: 'Failed to create tag', variant: 'destructive' }),
  });

  const createAndSelect = async (name: string, setter: (id: number) => void) => {
    const res: any = await createTagMutation.mutateAsync(name);
    if (res?.tag?.id) setter(res.tag.id);
  };

  const saveWorkflowMutation = useMutation({
    mutationFn: (payload: any) => {
      return apiRequest(
        isEditing ? `/api/admin/email/workflows/${workflow.id}` : '/api/admin/email/workflows',
        {
          method: isEditing ? 'PUT' : 'POST',
          data: payload
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/email/workflows'] });
      toast({
        title: isEditing ? "Workflow saved" : "Workflow created",
        description: `Your custom automated workflow has been successfully saved.`,
      });
      setOpen(false);
    },
    onError: (err) => {
      console.error(err);
      toast({
        title: "Failed to save workflow",
        description: "There was an error saving this workflow. Check inputs.",
        variant: "destructive"
      });
    }
  });

  const handleSave = () => {
    if (!workflowName.trim()) {
      toast({ title: "Name is required", variant: "destructive" });
      return;
    }
    if (!templateId) {
      toast({ title: "Please select/create an email template first", variant: "destructive" });
      return;
    }

    saveWorkflowMutation.mutate({
      name: workflowName,
      templateId: parseInt(templateId),
      triggerAction,
      status,
      frequency,
      notOpenedDays: triggerAction === 'still_not_opened' ? (notOpenedDays || 2) : null,
      notClickedDays: triggerAction === 'link_not_clicked' ? (notClickedDays || 2) : null,
      frequencyCount: frequency !== 'once' ? (frequencyCount || 1) : null,
      targetTagId,
      applyTagId,
      elseTemplateId: targetTagId && elseTemplateId ? parseInt(elseTemplateId) : null,
      elseApplyTagId: targetTagId && elseTemplateId ? elseApplyTagId : null,
      nodes: [],
      edges: []
    });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        {trigger}
      </DialogTrigger>
      <DialogContent className="w-[95vw] sm:w-[90vw] md:w-[850px] max-w-[95vw] max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Configure Email Workflow' : 'Orchestrate New Workflow'}</DialogTitle>
          <DialogDescription>
            Map triggering actions, configure the AI filters, and select target email contents using the interactive node board.
          </DialogDescription>
        </DialogHeader>

        <div className="py-2 w-full min-w-0 overflow-hidden">
          <WorkflowVisualCanvas
            workflowName={workflowName}
            setWorkflowName={setWorkflowName}
            templateId={templateId}
            setTemplateId={setTemplateId}
            triggerAction={triggerAction}
            setTriggerAction={setTriggerAction}
            templates={templates}
            allTags={allTags}
            targetTagId={targetTagId}
            setTargetTagId={setTargetTagId}
            applyTagId={applyTagId}
            setApplyTagId={setApplyTagId}
            onCreateTag={createAndSelect}
            elseTemplateId={elseTemplateId}
            setElseTemplateId={setElseTemplateId}
            elseApplyTagId={elseApplyTagId}
            setElseApplyTagId={setElseApplyTagId}
            workflowId={workflow?.id}
          />
        </div>

        {(triggerAction === 'still_not_opened' || triggerAction === 'link_not_clicked' || frequency !== 'once') && (
          <div className="flex items-center gap-6 border-t pt-4 mt-2 flex-wrap">
            {triggerAction === 'still_not_opened' && (
              <div className="flex items-center gap-2">
                <Label htmlFor="not-opened-days" className="text-xs font-semibold text-slate-600 whitespace-nowrap">
                  Resend if not opened after
                </Label>
                <Input
                  id="not-opened-days"
                  type="number"
                  min="1"
                  className="w-16 h-8 text-xs"
                  value={notOpenedDays}
                  onChange={e => setNotOpenedDays(e.target.value === '' ? '' : parseInt(e.target.value))}
                />
                <span className="text-xs text-slate-500 whitespace-nowrap">day(s)</span>
              </div>
            )}
            {triggerAction === 'link_not_clicked' && (
              <div className="flex items-center gap-2">
                <Label htmlFor="not-clicked-days" className="text-xs font-semibold text-slate-600 whitespace-nowrap">
                  Resend if link not clicked after
                </Label>
                <Input
                  id="not-clicked-days"
                  type="number"
                  min="1"
                  className="w-16 h-8 text-xs"
                  value={notClickedDays}
                  onChange={e => setNotClickedDays(e.target.value === '' ? '' : parseInt(e.target.value))}
                />
                <span className="text-xs text-slate-500 whitespace-nowrap">day(s)</span>
              </div>
            )}
            {frequency !== 'once' && (
              <div className="flex items-center gap-2">
                <Label htmlFor="frequency-count" className="text-xs font-semibold text-slate-600 whitespace-nowrap">
                  Times per {frequency === 'daily' ? 'day' : frequency === 'weekly' ? 'week' : 'month'}
                </Label>
                <Input
                  id="frequency-count"
                  type="number"
                  min="1"
                  className="w-16 h-8 text-xs"
                  value={frequencyCount}
                  onChange={e => setFrequencyCount(e.target.value === '' ? '' : parseInt(e.target.value))}
                />
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-between border-t pt-4 mt-2 flex-wrap gap-4">
          {/* Frequency selector */}
          <div className="flex items-center gap-3">
            <div className="text-xs font-semibold text-slate-600 whitespace-nowrap">Send Frequency:</div>
            <div className="flex gap-1">
              {[{value:'once',label:'Once'},{value:'daily',label:'Daily'},{value:'weekly',label:'Weekly'},{value:'monthly',label:'Monthly'}].map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setFrequency(opt.value)}
                  className={`px-3 py-1 text-xs rounded-full font-semibold border transition-all ${
                    frequency === opt.value
                      ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300 hover:text-indigo-600'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Switch 
                id="workflow-status" 
                checked={status === 'active'} 
                onCheckedChange={(checked) => setStatus(checked ? 'active' : 'draft')}
              />
              <Label htmlFor="workflow-status" className="text-xs text-slate-600 font-semibold cursor-pointer">
                {status === 'active' ? 'Active (Running)' : 'Draft (Paused)'}
              </Label>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saveWorkflowMutation.isPending}>
                {saveWorkflowMutation.isPending ? 'Saving...' : 'Save Workflow'}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Tag Picker — search-or-create popover shared by the per-row "add tag"
// button and the bulk "apply tag to selected" toolbar action.
export const TagPicker = ({
  allTags,
  appliedTagIds = [],
  onToggleTag,
  onCreateTag,
  trigger,
}: {
  allTags: { id: number; name: string; userCount: number }[];
  appliedTagIds?: number[];
  onToggleTag: (tagId: number, isApplied: boolean) => void;
  onCreateTag: (name: string) => void;
  trigger: React.ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');

  const filtered = allTags.filter(t => t.name.toLowerCase().includes(search.toLowerCase()));
  const exactMatch = allTags.some(t => t.name.toLowerCase() === search.trim().toLowerCase());

  const handleCreate = () => {
    if (!search.trim()) return;
    onCreateTag(search.trim());
    setSearch('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-56 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search or create tag..." value={search} onValueChange={setSearch} />
          <CommandList>
            {filtered.length === 0 && (
              <CommandEmpty className="p-0">
                {search.trim() ? (
                  <button
                    className="w-full text-left px-3 py-2 text-xs text-indigo-600 hover:bg-indigo-50 flex items-center gap-1.5"
                    onClick={handleCreate}
                  >
                    <Plus className="h-3 w-3" /> Create "{search.trim()}"
                  </button>
                ) : (
                  <div className="px-3 py-2 text-xs text-slate-400">No tags yet — type to create one.</div>
                )}
              </CommandEmpty>
            )}
            <CommandGroup>
              {filtered.map(tag => {
                const isApplied = appliedTagIds.includes(tag.id);
                return (
                  <CommandItem
                    key={tag.id}
                    onSelect={() => onToggleTag(tag.id, isApplied)}
                    className="text-xs cursor-pointer flex items-center justify-between"
                  >
                    <span className="flex items-center gap-1.5">
                      {isApplied && <Check className="h-3 w-3 text-indigo-600" />}
                      {tag.name}
                    </span>
                    <span className="text-[10px] text-slate-400">{tag.userCount}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {search.trim() && !exactMatch && filtered.length > 0 && (
              <div className="border-t p-1">
                <button
                  className="w-full text-left px-2 py-1.5 text-xs text-indigo-600 hover:bg-indigo-50 rounded flex items-center gap-1.5"
                  onClick={handleCreate}
                >
                  <Plus className="h-3 w-3" /> Create "{search.trim()}"
                </button>
              </div>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
};

// Segregated Users View Component
const SegregatedUsersView = () => {
  const [filter, setFilter] = useState<string>('all');
  const [searchQuery, setSearchQuery] = useState<string>('');

  const { data, isLoading, isError } = useQuery({
    queryKey: ['/api/admin/email/segregated-users'],
    queryFn: async () => {
      const response = await fetch('/api/admin/email/segregated-users');
      if (!response.ok) throw new Error('Failed to fetch segregated users');
      return response.json();
    }
  });

  if (isLoading) {
    return <div className="text-center py-12">Loading segregated users list...</div>;
  }

  if (isError) {
    return (
      <div className="text-center text-red-500 py-12 flex flex-col items-center justify-center">
        <AlertTriangle className="h-10 w-10 text-amber-500 mb-2" />
        <p className="font-semibold">Failed to load segregated users</p>
        <p className="text-xs text-slate-500 mt-1">Please check server database connection.</p>
      </div>
    );
  }

  const usersList = data?.users || [];

  // Counts
  const totalCount = usersList.length;
  const trialCount = usersList.filter((u: any) => u.category === 'trial').length;
  const expiredCount = usersList.filter((u: any) => u.category === 'expired').length;
  const expiringCount = usersList.filter((u: any) => u.category === 'expiring').length;
  const operationalCount = usersList.filter((u: any) => u.category === 'operational').length;

  // Filter & Search
  const filteredUsers = usersList.filter((u: any) => {
    const matchesFilter = filter === 'all' || u.category === filter;
    const matchesSearch =
      u.fullName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (u.tenantName && u.tenantName.toLowerCase().includes(searchQuery.toLowerCase()));
    return matchesFilter && matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Telemetry KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="border-slate-200/80 shadow-sm bg-slate-50/50">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Total Users</p>
              <h3 className="text-2xl font-bold text-slate-800 mt-1">{totalCount}</h3>
            </div>
            <div className="p-2.5 bg-slate-200/50 rounded-lg text-slate-600">
              <Mail className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-emerald-100 shadow-sm bg-emerald-50/20">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-emerald-600 uppercase tracking-wider">Trial Period</p>
              <h3 className="text-2xl font-bold text-emerald-700 mt-1">{trialCount}</h3>
            </div>
            <div className="p-2.5 bg-emerald-100/50 rounded-lg text-emerald-600">
              <CheckCircle2 className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-red-100 shadow-sm bg-red-50/20">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-red-600 uppercase tracking-wider">Expired License</p>
              <h3 className="text-2xl font-bold text-red-700 mt-1">{expiredCount}</h3>
            </div>
            <div className="p-2.5 bg-red-100/50 rounded-lg text-red-600">
              <AlertTriangle className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-amber-100 shadow-sm bg-amber-50/20">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-amber-600 uppercase tracking-wider">Expiring Soon</p>
              <h3 className="text-2xl font-bold text-amber-700 mt-1">{expiringCount}</h3>
            </div>
            <div className="p-2.5 bg-amber-100/50 rounded-lg text-amber-600">
              <Calendar className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>

        <Card className="border-indigo-100 shadow-sm bg-indigo-50/20">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold text-indigo-600 uppercase tracking-wider">Operational</p>
              <h3 className="text-2xl font-bold text-indigo-700 mt-1">{operationalCount}</h3>
            </div>
            <div className="p-2.5 bg-indigo-100/50 rounded-lg text-indigo-600">
              <Zap className="h-5 w-5" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filter and Search Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-4 bg-white p-4 rounded-xl border border-slate-200/80 shadow-sm">
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('all')}
            className="h-8 text-xs font-semibold"
          >
            All Users ({totalCount})
          </Button>
          <Button
            variant={filter === 'trial' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('trial')}
            className={`h-8 text-xs font-semibold ${filter !== 'trial' ? 'text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 border-emerald-200' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}`}
          >
            Trial Period ({trialCount})
          </Button>
          <Button
            variant={filter === 'expired' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('expired')}
            className={`h-8 text-xs font-semibold ${filter !== 'expired' ? 'text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200' : 'bg-red-600 hover:bg-red-700 text-white'}`}
          >
            Expired License ({expiredCount})
          </Button>
          <Button
            variant={filter === 'expiring' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('expiring')}
            className={`h-8 text-xs font-semibold ${filter !== 'expiring' ? 'text-amber-600 hover:text-amber-700 hover:bg-amber-50 border-amber-200' : 'bg-amber-600 hover:bg-amber-700 text-white'}`}
          >
            Expiring Soon ({expiringCount})
          </Button>
          <Button
            variant={filter === 'operational' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setFilter('operational')}
            className={`h-8 text-xs font-semibold ${filter !== 'operational' ? 'text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 border-indigo-200' : 'bg-indigo-600 hover:bg-indigo-700 text-white'}`}
          >
            Operational ({operationalCount})
          </Button>
        </div>

        <div className="w-full sm:w-72">
          <Input
            placeholder="Search name, email or company..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 text-xs"
          />
        </div>
      </div>

      {/* User Table */}
      <Card className="shadow-sm border-slate-200/80">
        <CardContent className="p-0">
          <div className="w-full">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/50">
                  <TableHead className="py-3 pl-4">User Details</TableHead>
                  <TableHead className="py-3">Organization</TableHead>
                  <TableHead className="py-3">Segment Category</TableHead>
                  <TableHead className="py-3 pr-4">License Status & Expiry</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-10 text-slate-500 text-sm">
                      No segregated users match this filter or search query.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user: any) => (
                    <TableRow key={user.id} className="hover:bg-slate-50/30">
                      <TableCell className="py-3 pl-4">
                        <div className="font-semibold text-slate-800 text-sm">{user.fullName}</div>
                        <div className="text-[10px] text-slate-500 mt-0.5 font-medium">{user.email}</div>
                      </TableCell>
                      <TableCell className="text-slate-700 py-3 text-xs font-semibold">{user.tenantName}</TableCell>
                      <TableCell className="py-3">
                        {user.category === 'trial' && (
                          <Badge className="bg-emerald-500 text-white border-0 hover:bg-emerald-600 text-xs">Trial Period</Badge>
                        )}
                        {user.category === 'expired' && (
                          <Badge className="bg-red-500 text-white border-0 hover:bg-red-600 text-xs">Expired License</Badge>
                        )}
                        {user.category === 'expiring' && (
                          <Badge className="bg-amber-500 text-white border-0 hover:bg-amber-600 text-xs">Expiring Soon</Badge>
                        )}
                        {user.category === 'operational' && (
                          <Badge variant="outline" className="text-indigo-700 bg-indigo-50 border-indigo-200 text-xs">Operational</Badge>
                        )}
                        {user.category === 'all' && (
                          <Badge variant="secondary" className="text-xs">Unclassified</Badge>
                        )}
                      </TableCell>
                      <TableCell className="py-3 pr-4">
                        <div className="inline-flex items-center gap-1.5">
                          <Badge variant="outline" className="capitalize text-[10px] py-0 px-1.5 h-5">
                            {user.status || 'Active'}
                          </Badge>
                          <span className="text-slate-500 text-[10px] font-semibold">
                            ({user.trialEndsAt ? new Date(user.trialEndsAt).toLocaleDateString() : 'Lifetime'})
                          </span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Workflow Recipients Dialog — shows, per user, whether they were sent this
// workflow's email and whether they've already opened it. Users marked
// "Opened" are the ones the engine will skip on the next scheduled run.
const WorkflowRecipientsDialog = ({ workflow, trigger }: { workflow: any; trigger: React.ReactNode }) => {
  const [open, setOpen] = useState(false);

  const { data, isLoading, isError } = useQuery({
    queryKey: [`/api/admin/email/workflows/${workflow.id}/recipients`],
    queryFn: async () => {
      const response = await fetch(`/api/admin/email/workflows/${workflow.id}/recipients`);
      if (!response.ok) throw new Error('Failed to fetch recipients');
      return response.json();
    },
    enabled: open,
  });

  const recipients = data?.recipients || [];
  const openedCount = recipients.filter((r: any) => r.isOpened).length;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Recipients — {workflow.name}</DialogTitle>
          <DialogDescription>
            {recipients.length === 0
              ? 'No emails sent yet for this pipeline.'
              : `${openedCount} of ${recipients.length} recipient(s) have opened the email and will be skipped on the next run.`}
          </DialogDescription>
        </DialogHeader>

        {isLoading && <div className="text-center py-6 text-sm text-slate-500">Loading recipients...</div>}
        {isError && <div className="text-center py-6 text-sm text-red-500">Failed to load recipients.</div>}

        {!isLoading && !isError && recipients.length > 0 && (
          <div className="max-h-96 overflow-y-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Recipient</TableHead>
                  <TableHead className="text-center">Times Sent</TableHead>
                  <TableHead>Last Sent</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recipients.map((r: any) => (
                  <TableRow key={r.recipientEmail}>
                    <TableCell className="text-sm">{r.recipientEmail}</TableCell>
                    <TableCell className="text-center text-sm">{r.timesSent}</TableCell>
                    <TableCell className="text-xs text-slate-500">
                      {r.lastSentAt ? new Date(r.lastSentAt).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell>
                      {r.isOpened ? (
                        <Badge className="bg-emerald-500 text-white hover:bg-emerald-600 text-[10px]">
                          Opened{r.openedAt ? ` · ${new Date(r.openedAt).toLocaleDateString()}` : ''} — won't resend
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-[10px] text-amber-600 border-amber-200 bg-amber-50">
                          Not opened yet
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

// ─── Advanced (graph) automation builder ────────────────────────────────────
// Unlike the fixed Trigger/Condition/Yes-No canvas above (one condition per
// workflow), this is a real node graph: add as many Condition/Action/Exit
// nodes as needed and wire them together freely. Every node position, every
// connection, and every node's config is persisted immediately through the
// graph CRUD API — nothing here is decorative.

export const NODE_TYPE_META: Record<string, { icon: React.ReactNode; badge: string; label: string }> = {
  trigger:   { icon: <Zap className="h-3.5 w-3.5" />,       badge: 'bg-indigo-500',  label: 'Trigger' },
  condition: { icon: <GitBranch className="h-3.5 w-3.5" />, badge: 'bg-amber-500',   label: 'Condition' },
  action:    { icon: <Mail className="h-3.5 w-3.5" />,      badge: 'bg-emerald-500', label: 'Action' },
  exit:      { icon: <LogOut className="h-3.5 w-3.5" />,    badge: 'bg-slate-400',   label: 'Exit' },
};

export const NodeConfigPanel = ({
  node, templates, allTags, onSave, onCreateTag,
}: {
  node: any;
  templates: any[];
  allTags: { id: number; name: string; userCount: number }[];
  onSave: (config: any) => void;
  onCreateTag: (name: string) => Promise<number>;
}) => {
  const [config, setConfig] = useState<any>(node.config || {});
  useEffect(() => { setConfig(node.config || {}); }, [node.id]);

  const update = (patch: any) => setConfig((c: any) => ({ ...c, ...patch }));
  const toggleTagIn = (field: string, tagId: number, isApplied: boolean) => {
    const current: number[] = config[field] || [];
    update({ [field]: isApplied ? current.filter((t: number) => t !== tagId) : [...current, tagId] });
  };
  const tagPickerFor = (field: string) => (
    <>
      <div className="flex flex-wrap gap-1 mb-1">
        {(config[field] || []).map((tid: number) => (
          <Badge key={tid} variant="outline" className="text-[10px]">{allTags.find(t => t.id === tid)?.name || '…'}</Badge>
        ))}
      </div>
      <TagPicker
        allTags={allTags}
        appliedTagIds={config[field] || []}
        onToggleTag={(tagId, isApplied) => toggleTagIn(field, tagId, isApplied)}
        onCreateTag={async (name) => { const id = await onCreateTag(name); toggleTagIn(field, id, false); }}
        trigger={<Button size="sm" variant="outline" className="h-7 text-xs w-full"><Plus className="h-3 w-3 mr-1" />Add tag</Button>}
      />
    </>
  );

  return (
    <div className="space-y-3">
      <div className="text-xs font-bold text-slate-700 uppercase">{node.type} node</div>

      {node.type === 'trigger' && (
        <>
          <div className="space-y-1">
            <Label className="text-xs">Entry template (optional)</Label>
            <select className="w-full text-xs border rounded p-1.5" value={config.templateId || ''} onChange={e => update({ templateId: e.target.value || undefined })}>
              <option value="">No template — enroll everyone</option>
              {templates?.map((t: any) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
          </div>
          {config.templateId && (
            <div className="space-y-1">
              <Label className="text-xs">Entry condition</Label>
              <select className="w-full text-xs border rounded p-1.5" value={config.triggerAction || 'still_not_opened'} onChange={e => update({ triggerAction: e.target.value })}>
                <option value="still_not_opened">Still Not Opened</option>
                <option value="link_not_clicked">Link Not Clicked</option>
                <option value="email_delivered">Delivered Successfully</option>
                <option value="email_opened">Email Opened</option>
              </select>
            </div>
          )}
          {config.triggerAction === 'still_not_opened' && config.templateId && (
            <div className="space-y-1">
              <Label className="text-xs">Days before considered "not opened"</Label>
              <Input type="number" min="1" className="h-7 text-xs" value={config.notOpenedDays ?? 2} onChange={e => update({ notOpenedDays: parseInt(e.target.value) || 2 })} />
            </div>
          )}
          {config.triggerAction === 'link_not_clicked' && config.templateId && (
            <div className="space-y-1">
              <Label className="text-xs">Days before considered "not clicked"</Label>
              <Input type="number" min="1" className="h-7 text-xs" value={config.notClickedDays ?? 2} onChange={e => update({ notClickedDays: parseInt(e.target.value) || 2 })} />
            </div>
          )}
        </>
      )}

      {node.type === 'condition' && (
        <>
          <div className="space-y-1">
            <Label className="text-xs">Match</Label>
            <div className="flex gap-1">
              {['any', 'all'].map(m => (
                <button key={m} onClick={() => update({ matchMode: m })} className={`px-2 py-1 text-[10px] rounded-full border font-semibold ${(config.matchMode || 'any') === m ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-slate-600 border-slate-200'}`}>{m.toUpperCase()}</button>
              ))}
            </div>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Has tag(s)</Label>
            {tagPickerFor('tagIds')}
          </div>
        </>
      )}

      {node.type === 'action' && (
        <>
          <div className="space-y-1">
            <Label className="text-xs">Action type</Label>
            <select className="w-full text-xs border rounded p-1.5" value={config.actionType || ''} onChange={e => update({ actionType: e.target.value })}>
              <option value="">Select…</option>
              <option value="send_email">Send Email</option>
              <option value="add_tags">Add Tag(s)</option>
              <option value="remove_tags">Remove Tag(s)</option>
              <option value="wait">Wait</option>
            </select>
          </div>
          {config.actionType === 'send_email' && (
            <div className="space-y-1">
              <Label className="text-xs">Template</Label>
              <select className="w-full text-xs border rounded p-1.5" value={config.templateId || ''} onChange={e => update({ templateId: e.target.value })}>
                <option value="">Select a template…</option>
                {templates?.map((t: any) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          )}
          {(config.actionType === 'add_tags' || config.actionType === 'remove_tags') && (
            <div className="space-y-1">
              <Label className="text-xs">Tag(s)</Label>
              {tagPickerFor('tagIds')}
            </div>
          )}
          {config.actionType === 'wait' && (
            <div className="space-y-1">
              <Label className="text-xs">Wait how many days</Label>
              <Input type="number" min="1" className="h-7 text-xs" value={config.waitDays ?? 1} onChange={e => update({ waitDays: parseInt(e.target.value) || 1 })} />
            </div>
          )}
        </>
      )}

      {node.type === 'exit' && <p className="text-xs text-slate-400">Nothing to configure — this ends the automation for anyone who reaches it.</p>}

      <Button size="sm" className="w-full" onClick={() => onSave(config)}>Save Node</Button>
    </div>
  );
};

// Manage Tags Dialog — lives on the Workflows tab (not Segregated Users):
// create tags, pick one to see who has it, add/remove individual users.
// This is the one place in the app tags are browsed/managed.
const ManageTagsPage = () => {
  const [selectedTagId, setSelectedTagId] = useState<number | null>(null);
  const [newTagName, setNewTagName] = useState('');
  const [addSearch, setAddSearch] = useState('');
  const queryClient = useQueryClient();

  const tagsQuery = useQuery({
    queryKey: ['/api/admin/tags'],
    queryFn: async () => {
      const response = await fetch('/api/admin/tags');
      if (!response.ok) throw new Error('Failed to fetch tags');
      return response.json();
    },
  });

  const tagsByUserQuery = useQuery({
    queryKey: ['/api/admin/tags/by-user'],
    queryFn: async () => {
      const response = await fetch('/api/admin/tags/by-user');
      if (!response.ok) throw new Error('Failed to fetch tags by user');
      return response.json();
    },
  });

  const usersQuery = useQuery({
    queryKey: ['/api/admin/email/segregated-users'],
    queryFn: async () => {
      const response = await fetch('/api/admin/email/segregated-users');
      if (!response.ok) throw new Error('Failed to fetch users');
      return response.json();
    },
  });

  const invalidateTagQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['/api/admin/tags'] });
    queryClient.invalidateQueries({ queryKey: ['/api/admin/tags/by-user'] });
  };

  const createTagMutation = useMutation({
    mutationFn: (name: string) => apiRequest('/api/admin/tags', { method: 'POST', data: { name } }),
    onSuccess: () => invalidateTagQueries(),
    onError: () => toast({ title: 'Failed to create tag', variant: 'destructive' }),
  });

  const applyTagMutation = useMutation({
    mutationFn: ({ tagId, userIds }: { tagId: number; userIds: number[] }) =>
      apiRequest(`/api/admin/tags/${tagId}/apply`, { method: 'POST', data: { userIds } }),
    onSuccess: () => invalidateTagQueries(),
    onError: () => toast({ title: 'Failed to apply tag', variant: 'destructive' }),
  });

  const removeTagMutation = useMutation({
    mutationFn: ({ tagId, userIds }: { tagId: number; userIds: number[] }) =>
      apiRequest(`/api/admin/tags/${tagId}/remove`, { method: 'POST', data: { userIds } }),
    onSuccess: () => invalidateTagQueries(),
    onError: () => toast({ title: 'Failed to remove tag', variant: 'destructive' }),
  });

  const deleteTagMutation = useMutation({
    mutationFn: (tagId: number) => apiRequest(`/api/admin/tags/${tagId}`, { method: 'DELETE' }),
    onSuccess: () => { invalidateTagQueries(); setSelectedTagId(null); },
    onError: () => toast({ title: 'Failed to delete tag', variant: 'destructive' }),
  });

  const handleCreateTag = () => {
    const name = newTagName.trim();
    if (!name) return;
    createTagMutation.mutate(name);
    setNewTagName('');
  };

  const allTags: { id: number; name: string; userCount: number }[] = tagsQuery.data?.tags || [];
  const tagsByUser: Record<number, { id: number; name: string }[]> = tagsByUserQuery.data?.tagsByUser || {};
  const allUsers = usersQuery.data?.users || [];

  const selectedTag = allTags.find(t => t.id === selectedTagId);
  const taggedUserIds = new Set(
    Object.entries(tagsByUser)
      .filter(([, tags]) => tags.some(t => t.id === selectedTagId))
      .map(([userId]) => parseInt(userId))
  );
  const taggedUsers = allUsers.filter((u: any) => taggedUserIds.has(u.id));
  const untaggedUsers = allUsers.filter((u: any) =>
    !taggedUserIds.has(u.id) &&
    (u.fullName.toLowerCase().includes(addSearch.toLowerCase()) || u.email.toLowerCase().includes(addSearch.toLowerCase()))
  );

  return (
    <div className="flex gap-4 min-h-[28rem]">
      {/* Tag list + create */}
      <div className="w-64 border-r pr-4 flex flex-col">
            <div className="flex gap-1 mb-3">
              <Input
                placeholder="New tag name"
                value={newTagName}
                onChange={e => setNewTagName(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleCreateTag(); }}
                className="h-8 text-xs"
              />
              <Button size="sm" className="h-8 px-2" onClick={handleCreateTag}><Plus className="h-3 w-3" /></Button>
            </div>
            <div className="flex-1 overflow-y-auto space-y-1">
              {allTags.length === 0 && <p className="text-xs text-slate-400 text-center py-6">No tags yet.</p>}
              {allTags.map(tag => (
                <button
                  key={tag.id}
                  onClick={() => setSelectedTagId(tag.id)}
                  className={`w-full text-left px-2 py-1.5 rounded-lg text-xs flex items-center justify-between ${
                    selectedTagId === tag.id ? 'bg-indigo-50 text-indigo-700 font-semibold' : 'hover:bg-slate-50 text-slate-700'
                  }`}
                >
                  <span className="truncate">{tag.name}</span>
                  <span className="text-slate-400">{tag.userCount}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Selected tag detail */}
          <div className="flex-1 min-w-0 flex flex-col">
            {!selectedTag ? (
              <div className="flex-1 flex items-center justify-center text-sm text-slate-400 text-center px-6">
                Select a tag on the left to see who has it, or create a new one.
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="text-sm font-semibold text-slate-800">{selectedTag.name} — {taggedUsers.length} user(s)</h4>
                  <Button variant="ghost" size="sm" className="h-7 text-xs text-red-500 hover:bg-red-50" onClick={() => deleteTagMutation.mutate(selectedTag.id)}>
                    <Trash2 className="h-3 w-3 mr-1" /> Delete tag
                  </Button>
                </div>
                <div className="border rounded-lg p-2 max-h-36 overflow-y-auto mb-3">
                  {taggedUsers.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-3">Nobody has this tag yet.</p>
                  ) : (
                    taggedUsers.map((u: any) => (
                      <div key={u.id} className="flex items-center justify-between px-1 py-1 text-xs">
                        <span>{u.fullName} <span className="text-slate-400">({u.email})</span></span>
                        <button onClick={() => removeTagMutation.mutate({ tagId: selectedTag.id, userIds: [u.id] })} className="text-slate-400 hover:text-red-600">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
                <Label className="text-xs font-semibold text-slate-600 mb-1">Add users to this tag</Label>
                <Input placeholder="Search users..." value={addSearch} onChange={e => setAddSearch(e.target.value)} className="h-8 text-xs mb-2" />
                <div className="border rounded-lg p-2 flex-1 overflow-y-auto">
                  {untaggedUsers.length === 0 ? (
                    <p className="text-xs text-slate-400 text-center py-3">No matching users.</p>
                  ) : (
                    untaggedUsers.map((u: any) => (
                      <div key={u.id} className="flex items-center justify-between px-1 py-1 text-xs hover:bg-slate-50 rounded">
                        <span>{u.fullName} <span className="text-slate-400">({u.email})</span></span>
                        <button onClick={() => applyTagMutation.mutate({ tagId: selectedTag.id, userIds: [u.id] })} className="text-indigo-500 hover:text-indigo-700">
                          <Plus className="h-3 w-3" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </>
            )}
          </div>
    </div>
  );
};

// Creates a new "advanced automation" (graph engine) workflow, then hands the
// caller its id so it can immediately open the GraphWorkflowEditor for it —
// mirrors WorkflowDialog's create flow but only needs a name up front, since
// everything else (trigger, conditions, actions) is built in the editor.
const NewGraphWorkflowDialog = ({ trigger, onCreated }: { trigger: React.ReactNode; onCreated: (workflowId: number) => void }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const queryClient = useQueryClient();

  const createMutation = useMutation({
    mutationFn: () => apiRequest('/api/admin/email/workflows/graph', { method: 'POST', data: { name, frequency: 'daily' } }),
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/email/workflows'] });
      setOpen(false);
      setName('');
      toast({ title: 'Advanced automation created', description: 'Now build out the graph: add nodes and connect them.' });
      onCreated(res.workflow.id);
    },
    onError: (err: any) => toast({ title: 'Failed to create automation', description: err?.message, variant: 'destructive' }),
  });

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>New Advanced Automation</DialogTitle>
          <DialogDescription>
            Build a fully custom node graph — trigger, conditions with multi-tag branching, and chained actions — like ConvertKit.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-2">
          <Label className="text-xs font-semibold text-slate-600">Automation name</Label>
          <Input
            placeholder="e.g. Link Click Follow-up"
            value={name}
            onChange={e => setName(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && name.trim()) createMutation.mutate(); }}
          />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button disabled={!name.trim() || createMutation.isPending} onClick={() => createMutation.mutate()}>
            {createMutation.isPending ? 'Creating…' : 'Create & Open Builder'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Workflow List Component
const WorkflowList = ({ templates }: { templates: any[] }) => {
  const queryClient = useQueryClient();
  const [, setLocation] = useLocation();
  const [testingId, setTestingId] = useState<number | null>(null);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['/api/admin/email/workflows'],
    queryFn: async () => {
      const response = await fetch('/api/admin/email/workflows');
      if (!response.ok) throw new Error('Failed to fetch workflows');
      return response.json();
    }
  });

  const tagsQuery = useQuery({
    queryKey: ['/api/admin/tags'],
    queryFn: async () => {
      const response = await fetch('/api/admin/tags');
      if (!response.ok) throw new Error('Failed to fetch tags');
      return response.json();
    }
  });
  const allTags: { id: number; name: string }[] = tagsQuery.data?.tags || [];

  const deleteWorkflowMutation = useMutation({
    mutationFn: (id: number) => {
      return apiRequest(`/api/admin/email/workflows/${id}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/email/workflows'] });
      toast({ title: "Workflow deleted" });
    }
  });

  const toggleStatusMutation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: string }) => {
      return apiRequest(`/api/admin/email/workflows/${id}`, {
        method: 'PUT',
        data: { status }
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/admin/email/workflows'] });
      toast({ title: "Workflow status updated" });
    }
  });

  const triggerTestMutation = useMutation({
    mutationFn: (id: number) => {
      return apiRequest(`/api/admin/email/workflows/${id}/trigger-test`, { method: 'POST' });
    },
    // Force immediate network refetch (not just stale mark) so sent count updates in real time
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['/api/admin/email/templates'] });
    },
    onSuccess: (res: any) => {
      // Force an immediate network refetch so the new tracking record shows up right away
      queryClient.refetchQueries({ queryKey: ['/api/admin/email/templates'] });
      toast({
        title: "Test Sent Successfully",
        description: res.message || "Simulated test workflow triggered successfully.",
      });
    },
    onError: (err: any) => {
      console.error(err);
      toast({
        title: "Failed to dispatch test",
        description: "Error launching test verification. Ensure Mailgun credentials are configured.",
        variant: "destructive"
      });
    }
  });

  const handleTestTrigger = (id: number) => {
    setTestingId(id);
    triggerTestMutation.mutate(id, {
      onSettled: () => setTestingId(null)
    });
  };

  if (isLoading) return <div className="text-center py-6">Loading active workflows...</div>;
  if (isError) return <div className="text-center text-red-500 py-6">Failed to load email workflows.</div>;

  if (!data?.workflows || data.workflows.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-center bg-slate-50/50 rounded-xl border border-dashed">
        <GitBranch className="h-12 w-12 text-slate-400 mb-3" />
        <h3 className="text-base font-bold text-slate-700">No pipelines generated</h3>
        <p className="text-xs text-slate-500 mt-1 max-w-sm">
          Map email events (like unopen, spam triggers, expiry notices) to templates and launch automated pipelines.
        </p>
      </div>
    );
  }

  return (
    <div className="w-full">
      <Table>
        <TableHeader>
          <TableRow className="bg-slate-50/50">
            <TableHead className="pl-4">Workflow & Trigger Event</TableHead>
            <TableHead>Associated Template</TableHead>
            <TableHead>Pipeline Status</TableHead>
            <TableHead className="text-right pr-4">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.workflows.map((wf: any) => {
            const matchedTemplate = templates?.find(t => t.id === wf.templateId);
            return (
              <TableRow key={wf.id} className="hover:bg-slate-50/30">
                <TableCell className="pl-4 py-3">
                  <div className="font-semibold text-slate-800 text-sm">{wf.name}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-1">
                    <Badge variant="outline" className="bg-indigo-50/50 border-indigo-100 text-indigo-700 text-[10px] font-mono py-0 px-1.5 h-4 inline-flex items-center">
                      {getTriggerActionLabel(wf.triggerAction)}
                      {wf.triggerAction === 'still_not_opened' && ` (${wf.notOpenedDays || 2}d)`}
                      {wf.triggerAction === 'link_not_clicked' && ` (${wf.notClickedDays || 2}d)`}
                    </Badge>
                    {wf.targetTagId && (
                      <Badge variant="outline" className="bg-slate-50 border-slate-200 text-slate-600 text-[10px] py-0 px-1.5 h-4 inline-flex items-center gap-1">
                        <TagIcon className="h-2.5 w-2.5" /> {allTags.find(t => t.id === wf.targetTagId)?.name || '…'}
                      </Badge>
                    )}
                    {wf.applyTagId && (
                      <Badge variant="outline" className="bg-emerald-50 border-emerald-200 text-emerald-700 text-[10px] py-0 px-1.5 h-4 inline-flex items-center gap-1">
                        <TagIcon className="h-2.5 w-2.5" /> +{allTags.find(t => t.id === wf.applyTagId)?.name || '…'} on send
                      </Badge>
                    )}
                    {wf.elseTemplateId && (
                      <Badge variant="outline" className="bg-slate-100 border-slate-300 text-slate-600 text-[10px] py-0 px-1.5 h-4 inline-flex items-center gap-1">
                        else → {templates?.find((t: any) => t.id === wf.elseTemplateId)?.name || 'Unknown template'}
                      </Badge>
                    )}
                  </div>
                </TableCell>
                <TableCell className="font-medium text-slate-600 py-3 text-xs">
                  {matchedTemplate ? matchedTemplate.name : 'Unknown template'}
                </TableCell>
                <TableCell className="py-3">
                  <div className="flex flex-col gap-1">
                    <div className="flex items-center gap-1.5">
                      <Badge variant={wf.status === 'active' ? 'default' : 'secondary'} className={wf.status === 'active' ? 'bg-emerald-500 text-white hover:bg-emerald-600 text-[10px]' : 'text-[10px]'}>
                        {wf.status === 'active' ? 'Active' : 'Paused'}
                      </Badge>
                      <Badge variant="outline" className="text-[10px] font-mono capitalize px-1.5 py-0 h-4 border-indigo-100 text-indigo-600 bg-indigo-50/40">
                        {wf.frequency || 'once'}{wf.frequency && wf.frequency !== 'once' ? ` ×${wf.frequencyCount || 1}` : ''}
                      </Badge>
                    </div>
                    {wf.lastTriggeredAt && (
                      <div className="text-[10px] text-slate-400 font-medium">
                        Last fired: {new Date(wf.lastTriggeredAt).toLocaleString()}
                      </div>
                    )}
                    {!wf.lastTriggeredAt && wf.status === 'active' && (
                      <div className="text-[10px] text-amber-500 font-medium">Pending first run</div>
                    )}
                  </div>
                </TableCell>
                <TableCell className="text-right pr-4 py-3">
                  <div className="inline-flex items-center gap-1.5 justify-end">
                    {wf.engineType === 'graph' ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs font-semibold text-indigo-600 border-indigo-100 hover:bg-indigo-50"
                        onClick={() => setLocation(`/admin/email/workflows/${wf.id}/automation`)}
                      >
                        <GitBranch className="h-3 w-3 mr-1" /> Edit Graph
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-8 text-xs font-semibold text-emerald-600 border-emerald-100 hover:bg-emerald-50"
                        disabled={testingId !== null}
                        onClick={() => handleTestTrigger(wf.id)}
                      >
                        <RefreshCw className={`h-3 w-3 mr-1 ${testingId === wf.id ? 'animate-spin' : ''}`} />
                        Test Trigger
                      </Button>
                    )}

                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-slate-500 hover:text-slate-800 hover:bg-slate-100"
                      onClick={() => toggleStatusMutation.mutate({ id: wf.id, status: wf.status === 'active' ? 'draft' : 'active' })}
                    >
                      {wf.status === 'active' ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 text-emerald-600" />}
                    </Button>

                    <WorkflowRecipientsDialog
                      workflow={wf}
                      trigger={
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-slate-800 hover:bg-slate-100" title="View recipients">
                          <Users className="h-4 w-4" />
                        </Button>
                      }
                    />

                    {wf.engineType !== 'graph' && (
                      <WorkflowDialog
                        workflow={wf}
                        templates={templates}
                        trigger={
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-500 hover:text-slate-800 hover:bg-slate-100">
                            <Edit className="h-4 w-4" />
                          </Button>
                        }
                      />
                    )}

                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                      onClick={() => deleteWorkflowMutation.mutate(wf.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

// Main component — owns the authoritative templates query so it stays alive across all tabs
export default function EmailTemplatesPage() {
  const { data: templateData, isLoading: templatesLoading, isError: templatesError } = useQuery({
    queryKey: ['/api/admin/email/templates'],
    queryFn: async () => {
      const response = await fetch('/api/admin/email/templates');
      if (!response.ok) throw new Error('Failed to fetch templates');
      return response.json();
    },
    refetchInterval: 10000,      // live-poll every 10s — active even when Templates tab is hidden
    refetchOnWindowFocus: true,  // re-fetch whenever user switches back to this browser tab
  });

  const templates = templateData?.templates || [];
  const [, setLocation] = useLocation();

  return (
    <SuperAdminLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-bold tracking-tight mb-1 text-slate-900">Email Hub</h1>
            <p className="text-sm text-slate-500">
              Manage reusable template blueprints and orchestrate automated event-based subscriber pipelines.
            </p>
          </div>
        </div>

        <Tabs defaultValue="templates" className="space-y-6">
          <TabsList className="bg-slate-100/80 p-1 border">
            <TabsTrigger value="templates" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <FileText className="h-4 w-4 mr-2" /> Templates Inventory
            </TabsTrigger>
            <TabsTrigger value="workflows" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <GitBranch className="h-4 w-4 mr-2" /> Automation Workflows
            </TabsTrigger>
            <TabsTrigger value="tags" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <TagIcon className="h-4 w-4 mr-2" /> Manage Tags
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
              <Users className="h-4 w-4 mr-2" /> Segregated Users
            </TabsTrigger>
          </TabsList>

          <TabsContent value="templates" className="mt-0">
            <Card className="shadow-sm border-gray-200">
              <CardHeader className="pb-3 border-b flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Templates Inventory</CardTitle>
                  <CardDescription>
                    Review configurations, scheduling rules, and delivery metrics for all blueprints.
                  </CardDescription>
                </div>
                <TemplateDialog 
                  trigger={
                    <Button variant="default" className="shadow-sm">
                      <Plus className="mr-2 h-4 w-4" /> New Template
                    </Button>
                  }
                />
              </CardHeader>
              <CardContent className="pt-4">
                <TemplateList data={templateData} isLoading={templatesLoading} isError={templatesError} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="workflows" className="mt-0">
            <Card className="shadow-sm border-gray-200">
              <CardHeader className="pb-3 border-b flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Event-Based Workflows</CardTitle>
                  <CardDescription>
                    Configure nodes to process custom trigger rules, filter logic, content formatting, and automated sending.
                  </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <NewGraphWorkflowDialog
                    trigger={
                      <Button variant="outline" className="shadow-sm text-indigo-600 border-indigo-200 hover:bg-indigo-50">
                        <GitBranch className="mr-2 h-4 w-4" /> New Advanced Automation
                      </Button>
                    }
                    onCreated={(id) => setLocation(`/admin/email/workflows/${id}/automation`)}
                  />
                  <WorkflowDialog
                    templates={templates}
                    trigger={
                      <Button variant="default" className="shadow-sm">
                        <Plus className="mr-2 h-4 w-4" /> Create Workflow
                      </Button>
                    }
                  />
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <WorkflowList templates={templates} />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="tags" className="mt-0">
            <Card className="shadow-sm border-gray-200">
              <CardHeader className="pb-3 border-b">
                <CardTitle>Manage Tags</CardTitle>
                <CardDescription>
                  Create tags, see who has each one, and add or remove people manually. Use these as Target Tag / Apply Tag when building a workflow.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <ManageTagsPage />
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="users" className="mt-0">
            <Card className="shadow-sm border-gray-200">
              <CardHeader className="pb-3 border-b">
                <CardTitle>Segregated Users Inventory</CardTitle>
                <CardDescription>
                  Review users categorized into Trial Period, Expired License, and Expiring Soon categories.
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <SegregatedUsersView />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </SuperAdminLayout>
  );
}